import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Form, Button, Alert, Nav, Tab } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { useToast } from '../context/ToastContext';
import { AuthContext } from '../context/AuthContext';
import { generateSketch } from '../utils/sketchGenerator';
import { flashFirmware, formatFlashError } from '../utils/espFlash';
import { BOARDS, DEFAULT_MQTT_HOST, DEFAULT_MQTT_PORT } from '../constants/deviceConfig';
import { canUseOta, canUseNotifications } from '../constants/subscriptionTiers';
import { useOtaTransfer } from '../hooks/useOtaTransfer';
import DeviceConfigPanel from '../components/device/DeviceConfigPanel';
import DeviceSensorsPanel from '../components/device/DeviceSensorsPanel';
import DeviceActionPinsPanel from '../components/device/DeviceActionPinsPanel';
import DeviceAutomationsPanel from '../components/device/DeviceAutomationsPanel';
import DeviceEmailNotificationsPanel from '../components/device/DeviceEmailNotificationsPanel';
import { normalizeSensors, normalizeActionPins } from '../utils/deviceIds';

import pako from 'pako';
window.pako = pako;

const socket = io(process.env.REACT_APP_API_URL || '');

const DeviceSetup = () => {
  const { token } = useContext(AuthContext);
  const [sensors, setSensors] = useState([]);
  const [actionPins, setActionPins] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [deviceType, setDeviceType] = useState('esp32');
  const [board, setBoard] = useState('ESP32 Dev Module');
  const [progress, setProgress] = useState('');
  const [programStatus, setProgramStatus] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [baudrate, setBaudrate] = useState(115200);
  const [flashMode, setFlashMode] = useState('dio');
  const [flashFreq, setFlashFreq] = useState('80m');
  const [flashSize, setFlashSize] = useState('4MB');
  const [emailNotifications, setEmailNotifications] = useState({
    enabled: false,
    alertRules: [],
    deviceSummary: { enabled: false, frequency: '1day' },
  });
  const [userSubTier, setUserSubTier] = useState('');
  const [deviceSecret, setDeviceSecret] = useState('');

  const navigate = useNavigate();
  const { deviceId } = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('connection');

  const getUserId = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch (err) {
      return null;
    }
  };

  const userId = getUserId();
  const otaAllowed = canUseOta(userSubTier);

  const buildSketch = () => generateSketch({
    sensors,
    actionPins,
    automations,
    userId,
    deviceId,
    wifiSsid,
    wifiPassword,
    deviceSecret,
    mqttServer: DEFAULT_MQTT_HOST,
    mqttPort: DEFAULT_MQTT_PORT,
  });

  const {
    otaState,
    otaInFlight,
    prepareOTAFirmware,
    abortOtaUpdate,
    clearOtaState,
  } = useOtaTransfer({
    socket,
    token,
    deviceId,
    buildSketch,
    board: BOARDS[deviceType][board],
    setProgress,
  });

  useEffect(() => {
    const loadDeviceData = async () => {
      try {
        const authToken = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response.data.deviceData && response.data.deviceData.length > 0) {
          const device = response.data.deviceData[0];
          setSensors(normalizeSensors(device.sensors || []));
          setActionPins(normalizeActionPins(device.actionPins || []));
          setDeviceName(device.deviceName || '');
          setAutomations(device.automations || []);
          setDeviceType(device.board?.[0]?.deviceType || 'esp32');
          setBoard(Object.keys(BOARDS[device.board?.[0]?.deviceType || 'esp32'])[0]);
          setWifiSsid(device.wifiSsid || '');
          setWifiPassword(device.wifiPassword || '');
          setDeviceSecret(device.deviceSecret || '');

          if (device.emailNotifications) {
            setEmailNotifications({
              enabled: device.emailNotifications.enabled || false,
              alertRules: device.emailNotifications.alertRules || [],
              deviceSummary: {
                enabled: device.emailNotifications.deviceSummary?.enabled || false,
                frequency: device.emailNotifications.deviceSummary?.frequency || '1day',
              },
            });
          }
        }
      } catch (err) {
        console.error('Failed to load device data:', err);
        const initialSensors = location.state?.sensors || [];
        const initialActionPins = location.state?.actionPins || [];
        const initialName = location.state?.deviceName || '';
        const initialAutomations = location.state?.automations || [];
        const initialDeviceType = location.state?.board?.deviceType || 'esp32';
        setSensors(normalizeSensors(initialSensors));
        setActionPins(normalizeActionPins(initialActionPins));
        setDeviceName(initialName);
        setAutomations(initialAutomations);
        setDeviceType(initialDeviceType);
        setBoard(Object.keys(BOARDS[initialDeviceType])[0]);
      }
    };

    const loadUserProfile = async () => {
      try {
        const authToken = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/user/profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setUserSubTier(response.data.subTier || 'free');
      } catch (err) {
        console.error('Failed to load user profile:', err);
        setUserSubTier('free');
      }
    };

    if (deviceId) {
      loadDeviceData();
      loadUserProfile();
    }
  }, [deviceId, location.state]);

  const programDevice = async () => {
    try {
      setProgramStatus('');
      const authToken = localStorage.getItem('token');
      const result = await flashFirmware({
        deviceId,
        sketch: buildSketch(),
        board: BOARDS[deviceType][board],
        flashMode,
        flashFreq,
        flashSize,
        baudrate,
        token: authToken,
        onProgress: setProgress,
      });
      setProgress(result);
      setProgramStatus(`✅ ${result}`);
    } catch (err) {
      console.error('Flash error:', err);
      const msg = await formatFlashError(err);
      setProgress(`Failed: ${msg}`);
      setProgramStatus(`Error: ${msg}`);
    }
  };

  const saveAll = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const payload = {
        sensors: normalizeSensors(sensors),
        actionPins: normalizeActionPins(actionPins),
        automations,
        deviceName,
        board,
        wifiSsid,
        wifiPassword,
      };

      if (canUseNotifications(userSubTier)) {
        payload.emailNotifications = emailNotifications;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setSuccess('All changes saved successfully!');
      setError('');
      toast.success('All changes saved successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save changes';
      setError(msg);
      setSuccess('');
      toast.error(msg);
    }
  };

  const handleSaveName = async () => {
    try {
      const authToken = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}`,
        { deviceName },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Failed to update device name:', err);
      setError('Failed to save device name. Please try again.');
    }
  };

  const showNotificationsTab = canUseNotifications(userSubTier);

  return (
    <>
      <div className="mb-4">
        {isEditing ? (
          <Form.Group className="d-flex align-items-center flex-wrap gap-2">
            <Form.Control
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '1.5rem', maxWidth: 400 }}
            />
            <Button variant="primary" onClick={handleSaveName}>Save</Button>
            {otaAllowed && (
              <Button
                onClick={prepareOTAFirmware}
                disabled={otaInFlight}
                style={{ backgroundColor: 'var(--teal)', borderColor: 'var(--teal)', color: 'white' }}
              >
                Send OTA Update
              </Button>
            )}
          </Form.Group>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <h3 className="mb-0 main" style={{ fontFamily: 'var(--font-display)', fontWeight: 500 }}>
              {deviceName}
            </h3>
            <PencilSquare
              size={20}
              style={{ cursor: 'pointer', color: 'var(--main-green)' }}
              onClick={() => setIsEditing(true)}
              title="Edit Name"
            />
          </div>
        )}
      </div>

      {otaInFlight && otaState && (
        <Alert variant="info" className="mb-3">
          OTA update in progress…
        </Alert>
      )}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'connection')}>
        <Nav variant="tabs" className="setup-tabs mb-4">
          <Nav.Item><Nav.Link eventKey="connection">Connection</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="sensors">Sensors</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="actuators">Actuators</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="automations">Automations</Nav.Link></Nav.Item>
          {showNotificationsTab && (
            <Nav.Item><Nav.Link eventKey="notifications">Notifications</Nav.Link></Nav.Item>
          )}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="connection">
            <DeviceConfigPanel
              deviceType={deviceType}
              setDeviceType={setDeviceType}
              board={board}
              setBoard={setBoard}
              wifiSsid={wifiSsid}
              setWifiSsid={setWifiSsid}
              wifiPassword={wifiPassword}
              setWifiPassword={setWifiPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              progress={progress}
              programStatus={programStatus}
              onProgramDevice={programDevice}
              onPrepareOta={prepareOTAFirmware}
              onAbortOta={abortOtaUpdate}
              otaAllowed={otaAllowed}
              otaInFlight={otaInFlight}
              otaState={otaState}
              onClearOta={clearOtaState}
              showAdvancedSettings={showAdvancedSettings}
              setShowAdvancedSettings={setShowAdvancedSettings}
              baudrate={baudrate}
              setBaudrate={setBaudrate}
              flashMode={flashMode}
              setFlashMode={setFlashMode}
              flashFreq={flashFreq}
              setFlashFreq={setFlashFreq}
              flashSize={flashSize}
              setFlashSize={setFlashSize}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="sensors">
            <DeviceSensorsPanel
              sensors={sensors}
              setSensors={setSensors}
              deviceType={deviceType}
              board={board}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="actuators">
            <DeviceActionPinsPanel
              sensors={sensors}
              actionPins={actionPins}
              setActionPins={setActionPins}
              deviceType={deviceType}
              board={board}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="automations">
            <DeviceAutomationsPanel
              sensors={sensors}
              actionPins={actionPins}
              automations={automations}
              setAutomations={setAutomations}
            />
          </Tab.Pane>
          {showNotificationsTab && (
            <Tab.Pane eventKey="notifications">
              <DeviceEmailNotificationsPanel
                sensors={sensors}
                emailNotifications={emailNotifications}
                setEmailNotifications={setEmailNotifications}
              />
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {success && <Alert variant="success" className="mt-3">{success}</Alert>}

      <div className="setup-sticky-footer d-flex gap-3">
        <Button variant="secondary" onClick={saveAll}>Save All Changes</Button>
        <Button variant="outline-primary" onClick={() => navigate('/app/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    </>
  );
};

export default DeviceSetup;